/* eslint-disable react/display-name */
import DataTable from 'react-data-table-component';
import SortIcon from '@material-ui/icons/ArrowDownward';
import {
  Checkbox,
  Chip,
  Grid,
  InputLabel,
  LinearProgress,
  TextField,
  Typography
} from '@material-ui/core';

// import local files
import RefreshButton from './refresh-button';
import { customStyles } from './customStyles';
import { Pagination } from '../../../molecules';
import { NoDataTableComponent, AsyncButton } from '../../../atoms';

// global constants
const SELECT_OPTIONS = [5, 10, 20, 25, 50];

/* Per Page Select Component */
const PerPageSelect = ({ handleChange, limit, queries }) => {
  const SelectFieldProps = {
    select: true,
    id: 'table_limit',
    name: 'limit',
    size: 'small',
    variant: 'outlined',
    value: limit || queries?.limit,
    onChange: handleChange,
    SelectProps: {
      displayEmpty: true,
      native: true
    }
  };

  return (
    <Grid item xl={4} lg={5} md={6} className="flex justify-start items-center">
      <InputLabel htmlFor="table_limit" className="mr-16 text-gray-700 font-semibold">
        Sayfa kayıt sayısı:
      </InputLabel>
      <TextField {...SelectFieldProps}>
        {SELECT_OPTIONS.map(option => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </TextField>
    </Grid>
  );
};

/* Custom Loader */
const CustomLoader = () => (
  <div className="p-32 pt-48 flex flex-col justify-center items-center space-y-10">
    <Typography variant="subtitle1" className="text-gray-500 font-semibold text-15">
      Yükleniyor...
    </Typography>
    <LinearProgress className="w-192 sm:w-320 max-w-full rounded-6 h-6" color="secondary" />
  </div>
);

/* No Data Component */
const NoDataComp = () => (
  <div className="border p-8 w-full">
    <NoDataTableComponent />
  </div>
);

const CustomDataTable = ({
  service,
  handleChange,
  queries,
  limit,
  skip,
  handlePageChange,
  handleSort,
  handleRefreshTable,
  addButton,
  handleDownBtnClick,
  downBtnLoading,
  noFilterSort = false,
  className,
  canDownload = false,
  defaultSortId,
  defaultDirection = 'asc',
  keyField = 'id',
  selectableRows = false,
  selectRow = undefined,
  selectSingleRow = false,
  noSelectAll = false
}) => {
  const currentPage = () => {
    if (skip === 0 || queries?.skip === 0) return 0;
    return Math.ceil(parseInt(skip || queries?.skip) / parseInt(limit || queries?.limit));
  };

  const renderButtons = () => {
    // eslint-disable-next-line valid-typeof
    if (typeof addButton === 'Object') {
      // eslint-disable-next-line no-lone-blocks
      {
        addButton.map(button => {
          return button;
        });
      }
    } else return addButton;
  };

  // *** DataTable Methods - Props ***
  const handleTableSort = (rowData, direction) => handleSort({ column: rowData?.id, direction });
  const handleRowClick = r => r?.clickEvent && r?.clickEvent();
  const handleSelectRow = ({ selectedRows }) => selectRow && selectRow(selectedRows);
  const selectProps = { indeterminate: isIndeterminate => isIndeterminate };

  // variables-states
  const sortDirection = defaultDirection === 'asc';
  const isDataLoading = !service.data && (!service.isInitialized || service.isLoading);
  const totalCount = service?.data?.totalCount;

  return (
    <>
      {/* Row per page and action buttons */}
      {!noFilterSort && (
        <Grid container className="flex justify-between items-center px-8 mb-8">
          <PerPageSelect handleChange={handleChange} limit={limit} queries={queries} />

          <Grid item xl={4} lg={5} md={6} className="flex justify-end items-center">
            {canDownload && (
              <AsyncButton
                icon="download"
                title="İndir"
                handleClick={handleDownBtnClick}
                loading={downBtnLoading}
                className="px-8"
              />
            )}

            {/* Action Buttons */}
            <div className="flex items-center">
              {/* Refresh Table Button */}
              <RefreshButton handleRefreshTable={handleRefreshTable} />

              {/* Other Action Buttons */}
              <div className="ml-8">{renderButtons()}</div>
            </div>
          </Grid>
        </Grid>
      )}

      {/* DATATABLE COMPONENT */}
      <DataTable
        /* Other */
        onRowClicked={handleRowClick || null}
        keyField={keyField}
        customStyles={customStyles}
        className={className}
        striped
        responsive
        pointerOnHover
        highlightOnHover
        /* Progress */
        progressPending={isDataLoading}
        progressComponent={<CustomLoader />}
        /* Header */
        fixedHeader
        fixedHeaderScrollHeight={'500px'}
        persistTableHead
        noHeader
        noContextMenu
        /* selectable */
        selectableRows={selectableRows}
        clearSelectedRows
        selectableRowsSingle={selectSingleRow}
        selectableRowsNoSelectAll={noSelectAll}
        selectableRowsComponent={Checkbox}
        onSelectedRowsChange={handleSelectRow}
        selectableRowsHighlight
        selectableRowsComponentProps={selectProps}
        /* sort - pagination */
        pagination={false}
        sortServer
        defaultSortFieldId={defaultSortId}
        defaultSortAsc={sortDirection}
        onSort={handleTableSort}
        sortIcon={<SortIcon />}
        /* remote data */
        columns={service?.data?.table?.columns || []}
        data={service?.data?.table?.rows || []}
        noDataComponent={<NoDataComp />}
      />

      {/* TotalCount info - Pagination */}
      <div className="flex justify-center items-center sm:justify-between sm:items-center flex-wrap">
        {!!totalCount && (
          <div className="flex items-center p-0 m-0 pl-8 mt-16 mr-8">
            <Typography variant="body1" className="mr-8 text-gray-700 font-semibold">
              Toplam Kayıt Sayısı:
            </Typography>

            <Chip
              className="p-8 mb-0 font-semibold"
              label={totalCount}
              size="small"
              variant="outlined"
              color="secondary"
            />
          </div>
        )}

        {/* Pagination */}
        {!noFilterSort && totalCount > 0 && (
          <Pagination
            total={Math.ceil(totalCount / parseInt(limit || queries?.limit))}
            current={currentPage()}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </>
  );
};

export default CustomDataTable;
